import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Network, Bot, BookOpen, FileText, ChevronLeft, ChevronRight, LogOut, ShieldCheck, Link2, Link2Off } from "lucide-react";
import logo from "@/assets/logo.png";
import { getSyncStatus } from "@/lib/atlasApi";

const NAV = [
  { to: "/teia", label: "Teia de Sabores", icon: Network, testId: "nav-item-teia" },
  { to: "/souschef", label: "SousChef AI", icon: Bot, testId: "nav-item-souschef" },
  { to: "/library", label: "Biblioteca Científica", icon: BookOpen, testId: "nav-item-library" },
  { to: "/drafts", label: "Fichas Exportadas", icon: FileText, testId: "nav-item-drafts" },
];

function roleLabel(roles = []) {
  const map = { admin: "Admin", chef: "Chef", researcher: "Researcher" };
  return roles.map((r) => map[r] || r).join(" / ") || "user";
}

export default function Sidebar({ user, onLogout, collapsed, onToggle, onNavigate }) {
  const [eco, setEco] = useState(null);
  useEffect(() => {
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const s = await getSyncStatus();
        if (!active) return;
        setEco(s);
        // Continua a sondar até conectar — o badge fica verde assim que os
        // insumos/fichas começam a retornar com sucesso do SOP & RAFT.
        if (!(s?.connected || s?.mirror_active || s?.authenticated)) timer = setTimeout(poll, 8000);
      } catch {
        if (!active) return;
        setEco({ connected: false });
        timer = setTimeout(poll, 8000);
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, []);
  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 68 : 260 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="h-full shrink-0 bg-[#0B0F17] border-r border-[#2A364F] flex flex-col overflow-hidden"
      data-testid="app-sidebar"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3.5 h-16 border-b border-[#2A364F]">
        <img src={logo} alt="FAWKES ATLAS" className="h-9 w-9 object-contain shrink-0" data-testid="sidebar-logo" />
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-bold tracking-tight text-[15px] leading-tight text-white truncate">FAWKES ATLAS</div>
            <div className="text-[10px] text-slate-500 truncate" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              Sua Enciclopédia Gastronômica · dev
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-1">
        {NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-[#161E2E] text-white"
                  : "text-slate-400 hover:text-slate-100 hover:bg-[#161E2E]/60"
              }`
            }
            title={collapsed ? label : undefined}
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-[#E05A2B]" />}
                <Icon className={`h-5 w-5 shrink-0 ${isActive ? "text-[#E05A2B]" : ""}`} />
                {!collapsed && <span className="truncate">{label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle (desktop) */}
      {onToggle && (
        <button
          data-testid="sidebar-toggle-button"
          onClick={onToggle}
          className="mx-2 mb-2 hidden md:flex items-center justify-center gap-2 rounded-lg py-2 text-slate-400 hover:text-[#E05A2B] hover:bg-[#161E2E]/60 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span className="text-xs">Recolher</span></>}
        </button>
      )}

      {/* Ecosystem sync status */}
      <div className={`px-3 pt-2 ${collapsed ? "flex justify-center" : ""}`} data-testid="ecosystem-status" title={eco?.note || "SOP & RAFT"}>
        {(!eco || eco?.connected || eco?.mirror_active || eco?.authenticated) ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-400">
            <Link2 className="h-3.5 w-3.5" />{!collapsed && "SOP & RAFT conectado"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-amber-500/80">
            <Link2Off className="h-3.5 w-3.5" />{!collapsed && "SOP & RAFT: aguardando conexão"}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-[#2A364F] p-3 mt-1" data-testid="sidebar-rbac-footer">
        <div className={`flex items-center gap-2.5 ${collapsed ? "justify-center" : ""}`}>
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-200 truncate">{user?.sub || user?.email || "Dev User"}</div>
              <div className="flex items-center gap-1 text-[10px] text-[#E05A2B]">
                <ShieldCheck className="h-3 w-3" />
                <span className="uppercase tracking-wider truncate">{roleLabel(user?.roles)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Terminar sessão / Trocar de conta — limpa tudo (token, cookies, PKCE) */}
        {collapsed ? (
          <button
            data-testid="sidebar-logout"
            onClick={onLogout}
            title="Terminar sessão / Trocar de conta"
            className="mt-2 mx-auto flex items-center justify-center text-slate-500 hover:text-[#FF007A] transition-colors"
          >
            <LogOut className="h-4 w-4" />
          </button>
        ) : (
          <button
            data-testid="sidebar-logout"
            onClick={onLogout}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#2A364F] py-2 text-[11px] text-slate-400 hover:text-[#FF007A] hover:border-[#FF007A]/40 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Terminar sessão / Trocar de conta
          </button>
        )}
      </div>
    </motion.aside>
  );
}
